import moment from 'moment';

import { zipCodeChange } from 'app/services/businessFunctions/helpers/zipCodeChange';
import {
  changeFieldValue,
  clearFieldValue,
  getQuestion,
  getSection,
  getTerminationGeneratorFormValues,
  setDictionaryEntryVisibility,
  setQuestionsVisibility,
  setMessageVisibility,
  setMaxDate,
  setQuestionVisibility,
} from 'app/services/businessFunctionHelpers';
import {
  DictionariesEnum,
  FieldsEnum,
  FormsEnum,
  SectionsEnum,
  TerminationReasonEnum,
  MessagesEnum,
} from 'app/shared/enums';
import { QuestionInterface } from 'app/shared/interfaces/Question.interface';

const formName = FormsEnum.TerminationGeneratorForm;

const setTerminationReasonAdditionalQuestionsVibilility = () => {
  const { noticeReason } = getTerminationGeneratorFormValues();

  const shouldShowAdditionalQuestions =
    noticeReason === TerminationReasonEnum.Article28a;

  setQuestionsVisibility(
    [
      FieldsEnum.TerminationInsurancePolicyStart,
      FieldsEnum.TerminationInsurerName,
    ],
    shouldShowAdditionalQuestions,
    formName
  );

  if (!shouldShowAdditionalQuestions) {
    clearFieldValue(FieldsEnum.TerminationInsurancePolicyStart, formName);
    clearFieldValue(FieldsEnum.TerminationInsurerName, formName);
  }
};

export const terminationGenerator = {
  onTerminationZipCodeChange(initialRun: boolean) {
    zipCodeChange(initialRun, FieldsEnum.TerminationZipCode, formName);
  },

  onTerminationInsurancePolicyEndingChange() {
    const { endDate } = getTerminationGeneratorFormValues();
    const currentDate = moment().startOf('day');
    const isPastDate = moment(endDate).isBefore(currentDate, 'day');
    const isToday = moment(endDate).isSame(currentDate);

    const dictionaryKey = `${DictionariesEnum.NoticeReason}_${FieldsEnum.TerminationReason}`;

    setDictionaryEntryVisibility({
      dictionaryKey,
      entryKey: TerminationReasonEnum.Article28,
      visible: !isToday && !isPastDate,
      formName,
    });

    setDictionaryEntryVisibility({
      dictionaryKey,
      entryKey: TerminationReasonEnum.Article31,
      visible: !isPastDate,
      formName,
    });

    if (isPastDate) {
      changeFieldValue(
        FieldsEnum.TerminationReason,
        TerminationReasonEnum.Article28a,
        formName
      );
    } else {
      clearFieldValue(FieldsEnum.TerminationReason, formName);
    }

    setMaxDate({
      formName,
      key: FieldsEnum.TerminationNoticeDate,
      maxDate: moment(endDate).format('YYYY-MM-DD'),
    });

    setTerminationReasonAdditionalQuestionsVibilility();
  },

  onTerminationReasonChange() {
    const { noticeReason } = getTerminationGeneratorFormValues();

    setQuestionVisibility({
      key: FieldsEnum.TerminationNoticeDate,
      visible: noticeReason === TerminationReasonEnum.Article31,
      formName,
    });

    setTerminationReasonAdditionalQuestionsVibilility();
  },

  fillAllRequiredTerminationConsents() {
    const { allConsentsChecked } = getTerminationGeneratorFormValues();

    const value = !!allConsentsChecked;

    const section = getSection(
      SectionsEnum.TerminationGeneratorConsents,
      formName
    );

    section.questions
      .map((questionKey: FieldsEnum) => getQuestion(questionKey, formName))
      .filter(
        (question: QuestionInterface) =>
          question.key.startsWith('consents.') &&
          question.key !== FieldsEnum.ButtonConsentsTerminationGenerator
      )
      .forEach((question: QuestionInterface) =>
        changeFieldValue(question.key, value, formName)
      );
  },

  requiredTerminationConsentChange() {
    changeFieldValue(
      FieldsEnum.ButtonConsentsTerminationGenerator,
      false,
      FormsEnum.TerminationGeneratorForm
    );
  },

  onTerminationEmailChange() {
    const forbiddenEmails = [
      'kontakt@pzu.pl',
      'wypowiedzenia@benefia.pl',
      'centrumklienta@generali.pl',
      'centrumklienta@proama.pl',
      'obsluga@uniqa.pl',
      'bok@aviva.pl',
      'wypowiedzenie.oc@interrisk.pl',
      'komunikacja@allianz.pl',
      'bok@link4.pl',
      'kontakt@wiener.pl',
      'wypowiedzeniaOC@warta.pl',
      'dok@balcia.pl',
      'info@ubezpieczeniapocztowe.pl',
      'korespondencja@compensa.pl',
      'wypowiedzenia@tuw.pl',
      'wypowiedzenia@tuz.pl',
      'wypowiedzenia@concordiaubezpieczenia.pl',
      'obsluga@beesafe.pl',
      'info@toleranceins.pl',
      'kontakt@trasti.pl',
      'kontakt@aviva.pl',
      'wypowiedzenia@reso.com.pl',
    ];

    const forbiddenDomains = [
      'warta.pl',
      'benefia.pl',
      'pzu.pl',
      'proama.pl',
      'generali.pl',
      'uniqa.pl',
      'aviva.pl',
      'allianz.pl',
      'link4.pl',
      'wiener.pl',
      'balcia.pl',
      'tuw.pl',
      'tuz.pl',
      'unext.pl',
      'toleranceins.pl',
      'beesafe.pl',
      'interrisk.pl',
      'mubi.pl',
      'compensa.pl',
    ];

    const { emailAddress } = getTerminationGeneratorFormValues();

    const isEmailContainsForbiddenDomain = forbiddenDomains.some((domain) =>
      emailAddress?.includes(domain)
    );

    emailAddress &&
      setMessageVisibility({
        key: MessagesEnum.EnterYourEmailToProceed,
        visible:
          forbiddenEmails.includes(emailAddress) ||
          isEmailContainsForbiddenDomain,
        formName,
      });
  },
};
